import { useState } from 'react'
import { Head, router, Link } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import DocumentUploadModal from '@features/file-operations/components/DocumentUploadModal'
import { formatBytes } from '@utils/formatBytes'
import type { DocumentsPageProps, Document } from '@shared-types/document'

export default function Index({ documents }: DocumentsPageProps) {
  const { t } = useTranslation()
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)

  const handleDelete = (documentId: string) => {
    if (confirm(t('documents.confirm_delete'))) {
      router.delete(`/documents/${documentId}`)
    }
  }

  const getFileIcon = (mimeType: string): string => {
    if (mimeType.startsWith('image/')) return '🖼️'
    if (mimeType === 'application/pdf') return '📄'
    if (mimeType.startsWith('video/')) return '🎥'
    if (mimeType.startsWith('audio/')) return '🎵'
    if (mimeType.includes('text')) return '📝'
    return '📎'
  }

  return (
    <>
      <Head title={t('documents.title')} />

      <div className="container mx-auto px-4 py-8">
        <div className="mb-4">
          <Link href="/" className="btn btn-ghost btn-sm">
            ← {t('common.backToHome')}
          </Link>
        </div>

        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">{t('documents.title')}</h1>
          <button
            onClick={() => setIsUploadModalOpen(true)}
            className="btn btn-primary"
          >
            {t('documents.upload_button')}
          </button>
        </div>

        {documents.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">{t('documents.empty_state')}</p>
            <button
              onClick={() => setIsUploadModalOpen(true)}
              className="btn btn-primary"
            >
              {t('documents.upload_first')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {documents.map((document: Document) => (
              <div key={document.id} className="card bg-base-100 shadow-xl">
                <figure className="px-10 pt-10 h-48 flex items-center justify-center bg-base-200">
                  {document.preview_url ? (
                    <img
                      src={document.preview_url}
                      alt={document.name}
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <span className="text-6xl">
                      {getFileIcon(document.mime_type)}
                    </span>
                  )}
                </figure>
                <div className="card-body">
                  <h2 className="card-title text-base truncate" title={document.name}>
                    {document.name}
                  </h2>
                  <div className="text-sm text-gray-500">
                    <p>
                      {formatBytes(document.size)} • {document.extension.toUpperCase()}
                    </p>
                    <p>{t('documents.owner')}: {document.owner.email}</p>
                    <p>
                      {new Date(document.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="card-actions justify-end mt-4">
                    <a
                      href={document.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-primary"
                    >
                      {t('documents.download')}
                    </a>
                    <button
                      onClick={() => handleDelete(document.id)}
                      className="btn btn-sm btn-error"
                    >
                      {t('documents.delete')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <DocumentUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
      />
    </>
  )
}
